<template>
    <div class="card dashboard-card p-4 text-white " :class="cardBgClass">

        <div class="large-icon-bg">
            <i :class="[iconClass, 'display-1']"></i>
        </div>

        <div class="card-content position-relative">

            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="card-title font-weight-bold mb-0">{{ title }}</h5>
                <span class="font-weight-bold h4 mb-0">{{ animatedMainValue }}</span>
            </div>

            <div class="progress-section">

                <div class="d-flex justify-content-between mb-1 align-items-center progress-labels">
                    <span class="font-weight-bold">{{ progressBarLabel1 }}</span>
                    <span class="font-weight-bold">{{ animatedProgressBarValue1 }}</span>
                </div>
                <div class="progress mb-3 progress-bar-lg">
                    <div
                        class="progress-bar bg-white"
                        role="progressbar"
                        :style="{ width: displayProgress1 + '%' }"
                        :aria-valuenow="progressBarValue1"
                        aria-valuemin="0"
                        aria-valuemax="100"
                    ></div>
                </div>

                <div >
                    <div class="d-flex justify-content-between mb-1 align-items-center progress-labels">
                        <span class="font-weight-bold">{{ progressBarLabel2 }}</span>
                        <span class="font-weight-bold">{{ animatedProgressBarValue2 }}</span>
                    </div>
                    <div class="progress mb-3 progress-bar-lg">
                        <div
                            class="progress-bar"
                            :class="cardVariantClass"
                            role="progressbar"
                            :style="{ width: displayProgress2 + '%' }"
                            :aria-valuenow="progressBarValue2"
                            aria-valuemin="0"
                            aria-valuemax="100"
                        ></div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
// Importer gsap si vous l'utilisez, sinon la transition de Vue sera plus simple mais moins fluide.
// Pour une solution Bootstrap/Vue pure, nous allons utiliser une fonction de comptage simple.

export default {
    name: 'DashboardCard',
    props: {
        title: { type: String, required: true },
        // On change le type à Number pour faciliter l'animation
        mainValue: { type: [Number, String], required: true },
        progressBarValue1: { type: [Number, String], required: true },
        progressBarValue2: { type: [Number, String], default: null },
        progressBarLabel1: { type: String, default: 'Total Reçu' },
        progressBarLabel2: { type: String, default: 'Non Reçu' },
        variant: {
            type: String,
            default: 'success',
            validator: (value) => ['success', 'primary', 'warning', 'danger'].includes(value),
        },
        iconClass: { type: String, required: false },
    },
    data() {
        return {
            // Variables pour l'animation des barres de progression
            displayProgress1: 0,
            displayProgress2: 0,

            // Variables pour l'animation des nombres
            animatedMainValue: 0,
            animatedProgressBarValue1: 0,
            animatedProgressBarValue2: 0,

            // Paramètres d'animation (pour la cohérence)
            animationDuration: 1500, // Durée en ms (1.5 seconde)
            steps: 50, // Nombre d'étapes d'incrémentation
        };
    },
    computed: {
        cardBgClass() {
            return `bg-gradient-${this.variant}  border-0`;
        },
        cardVariantClass() {
            return `bg-darken-${this.variant}`;
        },
        // Calculer les pourcentages réels (inchangé)
        progressPercent1() {
            return this.getProgressPercent(this.progressBarValue1);
        },
        progressPercent2() {
            return this.getProgressPercent(this.progressBarValue2);
        },
    },
    methods: {
        getProgressPercent(value) {
            const total = parseFloat(this.mainValue);
            const val = parseFloat(value);
            if (!total || isNaN(val)) return 0;
            return Math.min(((val / total) * 100), 100).toFixed(0);
        },

        // Fonction d'animation de comptage pour les nombres
        animateNumber(propName, finalValue) {
            const initialValue = 0;
            const target = finalValue;
            const stepTime = this.animationDuration / this.steps;
            const increment = (target - initialValue) / this.steps;
            let currentValue = initialValue;

            const timer = setInterval(() => {
                currentValue += increment;

                // Mettre à jour la variable d'animation
                this[propName] = currentValue.toFixed(0);

                // Si la valeur actuelle dépasse la cible, on arrête et fixe la valeur finale
                if (currentValue >= target) {
                    clearInterval(timer);
                    this[propName] = target.toFixed(0);
                }
            }, stepTime);
        },

        animateProgressBars() {
            this.$nextTick(() => {
                // Animation des barres (durée dans le CSS: 1.5s)
                setTimeout(() => {
                    this.displayProgress1 = this.progressPercent1;
                    if (this.progressBarValue2) {
                        this.displayProgress2 = this.progressPercent2;
                    }
                }, 50);
            });
        },

        animateAll() {
            const mainValueNum = parseFloat(this.mainValue);
            const val1Num = parseFloat(this.progressBarValue1);

            // 1. Animer les Nombres
            this.animateNumber('animatedMainValue', mainValueNum);
            this.animateNumber('animatedProgressBarValue1', val1Num);

            if (this.progressBarValue2) {
                const val2Num = parseFloat(this.progressBarValue2);
                this.animateNumber('animatedProgressBarValue2', val2Num);
            }

            // 2. Animer les Barres de Progression
            this.animateProgressBars();
        }
    },
    mounted() {
        this.animateAll();
    },
    watch: {
        // Optionnel : Ré-animer si les props changent
        mainValue() {
            this.animateAll();
        },
    },
};
</script>

<style scoped>
/* Conserver la transition pour les barres de progression */
.progress-bar {
    transition: width 1.5s ease-out; /* Assurez-vous que cette durée correspond à animationDuration */
}

/* Votre style pour l'icône, les dégradés et la taille des barres */
.dashboard-card {
    border-radius: 0.5rem;
    position: relative;
    overflow: hidden;
}

/* Dégradés */
.bg-gradient-success { background-image: linear-gradient(135deg, #4CAF50 0%, #81C784 100%); }
.bg-gradient-primary { background-image: linear-gradient(135deg, #2196F3 0%, #64B5F6 100%); }
/* MODIFICATION 1 : Le dégradé principal utilise votre couleur. J'ai ajouté une couleur plus claire pour un dégradé subtil. */
.bg-gradient-warning { background-image: linear-gradient(135deg, #076360 0%, #0A8B88 100%); }
.bg-gradient-danger { background-image: linear-gradient(135deg, #F44336 0%, #E57373 100%); }

.bg-darken-success { background-color: #2E7D32; }
.bg-darken-primary { background-color: #1565C0; }
/* MODIFICATION 2 : La couleur de la deuxième barre de progression (le "darken") */
.bg-darken-warning { background-color: #03413E; }
.bg-darken-danger { background-color: #C62828; }

.progress-bar-lg {
    height: 10px !important;
}

.progress {
    background-color: rgba(255, 255, 255, 0.3);
}

.large-icon-bg {
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
    opacity: 0.2;
    z-index: 1;
}
</style>